import {
  Badge,
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { Bell, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getNotifications,
  markNotificationsAsRead,
} from "../../api/notifications";
import { Logo } from "../../components/Logo";

const NotificationsDropdown = () => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);

  const markReadIfOpen = async () => {
    if (isOpen && notificationCount > 0) {
      // Dropdown is open and user is closing it → mark as read
      await handleMarkAllAsRead();
    }
  };

  const toggleDropdown = async () => {
    await markReadIfOpen();
    setIsOpen(!isOpen);
  };

  const handleClickAway = async () => {
    await markReadIfOpen();
    setIsOpen(false);
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await getNotifications();
        setNotifications(response.notifications);
        setNotificationCount(response.unread_count);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
  }, []);

  const handleMarkAllAsRead = async () => {
    try {
      await markNotificationsAsRead();
      const response = await getNotifications();
      setNotifications(response.notifications);
      setNotificationCount(response.unread_count);
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box>
          <IconButton
            onClick={toggleDropdown}
            aria-label="show notifications"
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <Badge
              badgeContent={notificationCount}
              showZero={false}
              color="error"
              overlap="circular"
              componentsProps={{
                badge: {
                  sx: {
                    top: "auto",
                    bottom: 0,
                    right: 0,
                    transform: "translate(50%, 50%)",
                    fontSize: 9,
                    borderRadius: "0.375rem",
                    minWidth: 17,
                    height: 15.3,
                  },
                },
              }}
            >
              <Bell size={20} strokeWidth={1.5} />
            </Badge>
          </IconButton>

          {isOpen && (
            <Box
              sx={{
                position: "absolute",
                top: "100%",
                right: 0,
                width: { xs: "calc(100vw - 32px)", sm: 350 },
                maxHeight: { xs: "60vh", sm: 400 },
                bgcolor: "background.paper",
                boxShadow: 3,
                borderRadius: 1,
                mt: 1,
                zIndex: 1300,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle1" sx={{ px: 2, py: 1 }}>
                {t("notifications.title")} ({notifications.length})
              </Typography>
              <Divider />
              <List
                sx={{
                  overflowY: "auto",
                  minHeight: 270,
                }}
              >
                {notifications.map((notification, idx) => (
                  <ListItem key={idx} alignItems="center" sx={{ py: 1 }}>
                    <ListItemAvatar>
                      <Logo style={{ width: 30 }} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          fontWeight={notification.is_read ? "normal" : "bold"}
                        >
                          {notification.title}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              <Divider />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 2, py: 1 }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ cursor: "pointer", color: "primary.main" }}
                >
                  <Mail size={18} />
                  <Typography variant="bodySm">
                    {t("notifications.inbox")}
                  </Typography>
                </Stack>
                <Typography
                  variant="bodySm"
                  sx={{ cursor: "pointer", color: "primary.main" }}
                  onClick={handleMarkAllAsRead}
                >
                  {t("notifications.markAllAsRead")}
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </ClickAwayListener>
    </Box>
  );
};

export default NotificationsDropdown;
